import { useState, useEffect } from "react";
import { ApiClient } from "@/services/api";
import { Card, Button, Alert, LoadingSpinner } from "@/components/shared";

interface BoostData {
  id: string;
  suburb: string;
  boost_type: string;
  status: string;
  start_date: string;
  end_date: string;
  ranking_multiplier: number;
}

const BOOST_OPTIONS = [
  { type: "daily", label: "1 Day", price: "R49" },
  { type: "weekly", label: "7 Days", price: "R199" },
  { type: "monthly", label: "30 Days", price: "R499" },
];

export function BoostManager() {
  const [boosts, setBoosts] = useState<BoostData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suburb, setSuburb] = useState("");
  const [selectedType, setSelectedType] = useState("weekly");
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const api = new ApiClient();
        const data = await api.get<{ data: BoostData[] }>("/boosts/me");
        setBoosts(data.data);
      } catch (err: any) {
        // 404 = no agent profile/boosts yet, not an error
        if (!err.message?.includes("404")) setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handlePurchase = async () => {
    if (!suburb.trim()) return;
    setPurchasing(true);
    setError(null);
    try {
      const api = new ApiClient();
      const result = await api.post<{ authorization_url?: string; action?: string }>("/boosts", {
        suburb: suburb.trim(),
        boost_type: selectedType,
      });

      if (result.authorization_url) {
        window.location.href = result.authorization_url;
      } else if (result.action === "boost_activated") {
        window.location.reload();
      }
    } catch (err: any) {
      setError(err.message);
      setPurchasing(false);
    }
  };

  const activeBoosts = boosts.filter((b) => b.status === "active" && new Date(b.end_date) > new Date());

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {error && <Alert type="error">{error}</Alert>}

      {/* Active boosts */}
      {activeBoosts.length > 0 && (
        <Card>
          <h3 className="font-bold text-white mb-3">Active Boosts</h3>
          <div className="space-y-2">
            {activeBoosts.map((b) => {
              const remaining = Math.max(0, Math.ceil((new Date(b.end_date).getTime() - Date.now()) / (1000 * 60 * 60)));
              return (
                <div key={b.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div>
                    <p className="font-medium text-white">{b.suburb}</p>
                    <p className="text-sm text-white/50">{b.boost_type} boost • {b.ranking_multiplier}x ranking</p>
                  </div>
                  <span className="text-sm text-teal-400">
                    {remaining > 24 ? `${Math.ceil(remaining / 24)}d left` : `${remaining}h left`}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Purchase new boost */}
      <Card>
        <h3 className="font-bold text-white mb-3">Purchase Boost</h3>
        <p className="text-sm text-white/60 mb-3">
          Boost your ranking in a specific suburb to appear higher in search results.
        </p>

        <div className="space-y-3">
          <input
            type="text"
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            placeholder="Enter suburb name..."
            className="w-full input"
          />

          <div className="grid grid-cols-3 gap-2">
            {BOOST_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                onClick={() => setSelectedType(opt.type)}
                className={`p-3 rounded-lg border text-center transition-colors ${
                  selectedType === opt.type
                    ? "bg-teal-500 text-white border-teal-500"
                    : "bg-midnight text-white/70 border-white/15 hover:border-gold/40"
                }`}
              >
                <p className="font-semibold">{opt.label}</p>
                <p className="text-sm">{opt.price}</p>
              </button>
            ))}
          </div>

          <Button
            variant="primary"
            onClick={handlePurchase}
            disabled={purchasing || !suburb.trim()}
            className="w-full"
          >
            {purchasing ? <LoadingSpinner size="sm" /> : "Purchase Boost"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
